import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import MobileHeader from "../../components/MobileHeader";
import { api, formatApiError, formatCDF } from "../../lib/api";
import { Badge, Button, Card, Input, StatusBadge } from "../../components/ui-kit";
import { CheckCircle, ClipboardText, Lifebuoy, Package, ShieldCheck, Truck } from "@phosphor-icons/react";

function paymentTone(status) {
  if (status === "CONFIRMED") return "success";
  if (status === "REJECTED" || status === "FAILED") return "danger";
  if (status === "PROOF_SUBMITTED") return "primary";
  return "warning";
}

const PERMISSION_LABELS = {
  support: "Support client",
  merchant_onboarding: "Partenaires",
  driver_onboarding: "Chauffeurs",
  delivery_ops: "Operations livraison",
  finance: "Finance terrain",
};

export default function AgentDashboard() {
  const [summary, setSummary] = useState(null);
  const [tasks, setTasks] = useState({ merchant_orders: [], deliveries: [], pending_documents: [], disputes: [] });
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");

  const permissions = summary?.agent?.permissions || [];
  const canConfirmDelivery = permissions.includes("delivery_ops");

  const load = async () => {
    try {
      const [meRes, taskRes] = await Promise.all([
        api.get("/agent/me"),
        api.get("/agent/tasks"),
      ]);
      setSummary(meRes.data);
      setTasks({
        merchant_orders: Array.isArray(taskRes.data?.merchant_orders) ? taskRes.data.merchant_orders : [],
        deliveries: Array.isArray(taskRes.data?.deliveries) ? taskRes.data.deliveries : [],
        pending_documents: Array.isArray(taskRes.data?.pending_documents) ? taskRes.data.pending_documents : [],
        disputes: Array.isArray(taskRes.data?.disputes) ? taskRes.data.disputes : [],
      });
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    load();
    const timer = setInterval(load, 10000);
    return () => clearInterval(timer);
  }, []);

  const kpis = useMemo(() => [
    { label: "Commandes ouvertes", value: summary?.open_orders || 0, icon: Package, tone: "primary" },
    { label: "Livraisons suivies", value: tasks.deliveries.length, icon: Truck, tone: "success" },
    { label: "Documents en attente", value: tasks.pending_documents.length, icon: ClipboardText, tone: "warning" },
    { label: "Litiges ouverts", value: summary?.open_disputes || 0, icon: Lifebuoy, tone: "danger" },
  ], [summary, tasks.deliveries.length, tasks.pending_documents.length]);

  const confirmOrder = async (orderId) => {
    setBusy(orderId);
    try {
      await api.post(`/agent/merchant-orders/${orderId}/confirm`, { note: note.trim() || null });
      toast.success("Livraison creee et livreur recherche");
      setNote("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="app-screen overflow-x-hidden bg-premium-grad text-foreground">
      <MobileHeader title="Agent" />
      <main className="mx-auto w-full max-w-6xl space-y-5 px-4 pb-24 pt-3 sm:px-6 lg:px-8">
        <section className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-primary">Espace agent Nexora</div>
            <h1 className="font-heading text-3xl font-black tracking-tighter">{summary?.agent?.name || "Agent terrain"}</h1>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
              Suivi terrain, commandes partenaires, livraisons, documents et litiges. Les permissions sont controlees par l'admin.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {(permissions.length ? permissions : ["support"]).map((permission) => (
              <Badge key={permission} tone="primary">{PERMISSION_LABELS[permission] || permission}</Badge>
            ))}
          </div>
        </section>

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {kpis.map((kpi) => {
            const Icon = kpi.icon;
            return (
              <Card key={kpi.label} className="p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">{kpi.label}</div>
                    <div className="font-heading text-3xl font-black">{kpi.value}</div>
                  </div>
                  <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10 text-primary">
                    <Icon size={22} weight="duotone" />
                  </span>
                </div>
              </Card>
            );
          })}
        </div>

        <Card className="p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-secondary/15 text-secondary">
                <ShieldCheck size={22} weight="duotone" />
              </span>
              <div>
                <h2 className="font-heading text-xl font-black">Actions rapides</h2>
                <p className="text-xs text-muted-foreground">La note est ajoutee aux validations que vous effectuez.</p>
              </div>
            </div>
            <Badge tone={summary?.agent?.active === false ? "danger" : "success"}>
              {summary?.agent?.active === false ? "Inactif" : "Actif"}
            </Badge>
          </div>
          <Input className="mt-4" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note terrain optionnelle" />
        </Card>

        <div className="grid gap-5 xl:grid-cols-[1.15fr_0.85fr]">
          <Card className="overflow-hidden">
            <div className="border-b border-white/10 p-4">
              <h2 className="font-heading text-xl font-black">Commandes partenaires a suivre</h2>
              <p className="text-xs text-muted-foreground">Confirmez uniquement quand le paiement/preuve est valide et que le partenaire est pret.</p>
            </div>
            <div className="divide-y divide-white/10">
              {tasks.merchant_orders.map((order) => (
                <div key={order.id} className="p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-[10px] font-black uppercase tracking-widest text-primary">#{String(order.id).slice(0, 8).toUpperCase()}</div>
                      <h3 className="font-heading text-lg font-black">{order.merchant_name}</h3>
                      <p className="text-xs text-muted-foreground">{order.customer_name || "Client"} - {order.city_name || "Ville"}</p>
                    </div>
                    <div className="flex flex-wrap justify-end gap-2">
                      <StatusBadge status={order.status} />
                      <Badge tone={paymentTone(order.payment_status)}>{order.payment_status}</Badge>
                    </div>
                  </div>
                  <div className="mt-3 grid gap-2 rounded-2xl bg-white/5 p-3 text-sm sm:grid-cols-3">
                    <div>
                      <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Total</div>
                      <div className="font-mono font-black">{formatCDF(order.total_amount || order.items_subtotal)}</div>
                    </div>
                    <div>
                      <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Paiement</div>
                      <div className="font-bold">{order.payment_method}</div>
                    </div>
                    <div>
                      <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Preuve</div>
                      {order.payment_proof_url ? (
                        <a href={order.payment_proof_url} target="_blank" rel="noreferrer" className="font-bold text-primary underline">Voir</a>
                      ) : (
                        <span className="text-muted-foreground">Aucune</span>
                      )}
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button
                      disabled={!canConfirmDelivery || busy === order.id}
                      onClick={() => confirmOrder(order.id)}
                    >
                      <CheckCircle size={16} weight="bold" /> Creer livraison
                    </Button>
                    {!canConfirmDelivery && (
                      <Badge tone="warning">Permission livraison requise</Badge>
                    )}
                  </div>
                </div>
              ))}
              {tasks.merchant_orders.length === 0 && (
                <div className="p-8 text-center text-sm text-muted-foreground">Aucune commande ouverte dans votre zone.</div>
              )}
            </div>
          </Card>

          <div className="grid gap-5">
            <Card className="overflow-hidden">
              <div className="border-b border-white/10 p-4">
                <h2 className="font-heading text-xl font-black">Livraisons en cours</h2>
              </div>
              <div className="divide-y divide-white/10">
                {tasks.deliveries.slice(0, 12).map((ride) => (
                  <div key={ride.id} className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate font-bold">{ride.customer_name || ride.recipient_name || "Client"}</div>
                        <div className="text-xs text-muted-foreground">{ride.pickup?.address || ride.origin_label} vers {ride.destination?.address || ride.destination_label}</div>
                      </div>
                      <StatusBadge status={ride.status} />
                    </div>
                  </div>
                ))}
                {tasks.deliveries.length === 0 && <div className="p-6 text-sm text-muted-foreground">Aucune livraison active.</div>}
              </div>
            </Card>

            <Card className="overflow-hidden">
              <div className="border-b border-white/10 p-4">
                <h2 className="font-heading text-xl font-black">Documents et litiges</h2>
              </div>
              <div className="grid gap-3 p-4">
                <div className="rounded-2xl border border-white/10 bg-white/5 p-3">
                  <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Documents chauffeur</div>
                  <div className="font-heading text-2xl font-black">{tasks.pending_documents.length}</div>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/5 p-3">
                  <div className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Litiges support</div>
                  <div className="font-heading text-2xl font-black">{tasks.disputes.length}</div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
